import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { ref, query, orderByChild, equalTo, onValue, update } from 'firebase/database';
import { db } from '../firebase';
import { useConfig } from '../context/ConfigContext';
import { ADMIN_COLORS } from '../constants/bookingConst';
import { computeHourlyEquipmentUsage } from '../utils/equipmentAvailabilityUtil';
import { parseTimeSlotStartHour, formatHourLabel, formatTimeSlotRange, sortTimeSlots, getAllConfiguredTimeSlots } from '../utils/bookingUtils';
import '../css/AdminEquipment.css';
import { formatMoney } from '../utils/currencyUtil';

// Shared jassen light palette (constants/bookingConst.js)
const COLORS = ADMIN_COLORS;

// Bookings still worth showing to the admin as an active equipment rental —
// mirrors the app-wide status lifecycle ('awaiting_payment' -> 'pending' ->
// 'confirmed'/'disapproved', or 'cancelled' at any point). Cancelled/
// disapproved bookings never actually take equipment out, and
// 'awaiting_payment' ones may still lapse, so only these two count.
const ACTIVE_RENTAL_STATUSES = ['pending', 'confirmed'];

// Does this booking's time-slot selection include the given slot? Handles
// Firebase RTDB's habit of turning array values back into plain objects on
// read (selectedSlots is stored as { timeSlot: [court, ...] }).
const bookingHasSlot = (booking, slot) => {
  const courts = booking.timeSlots?.[slot];
  if (!courts) return false;
  return Array.isArray(courts) ? courts.length > 0 : Object.keys(courts).length > 0;
};

/**
 * Admin "Equipment" screen — everything scoped to one selected date (and,
 * within that, one or more selected time slots) at a time, so this never
 * scans the whole bookings table the way a historical export would need to.
 * Equipment gets *returned* once a booking's slot passes, exactly like a
 * court does — so "how many paddles are free" only makes sense for a
 * specific hour, not "today" as a whole. This mirrors the same per-slot
 * availability model TimeSlotSelection.jsx uses for courts:
 *
 *  1. Inventory & availability — only shown once at least one time slot is
 *     selected, since "how many paddles are free" isn't meaningful without
 *     a specific hour in mind. Total units owned (set in Config ->
 *     Equipment) minus the busiest of the selected hours' online holds,
 *     minus a manually-entered "walk-in rentals" count for that same
 *     selection — edited as a draft with explicit Save/Clear, so nothing
 *     is written until the admin confirms it.
 *  2. Equipment rentals list — every active (pending/confirmed) booking
 *     that rented equipment that day. With no filter, it's a flat list for
 *     the whole day; pick slot(s) to split it into one section per slot,
 *     showing only the bookings that actually cover that hour.
 */
function AdminEquipment() {
  const { config } = useConfig();
  const [selectedDateStr, setSelectedDateStr] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [bookingsForDate, setBookingsForDate] = useState([]);
  const [holdsByEquipment, setHoldsByEquipment] = useState({});
  // equipmentManualUsage/{date}/{equipmentId}/{hour} = qty — per-hour, not a
  // flat day total, so it can be scoped to whatever time slot(s) are
  // selected (see manualUsagePeak-equivalent logic in equipmentSummary).
  const [manualUsage, setManualUsage] = useState({});
  // Unsaved edits to the walk-in input, keyed by equipment id — kept
  // separate from `manualUsage` (the last-saved value) so Save/Clear has
  // something to compare against and revert to.
  const [walkinDraft, setWalkinDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  // Every hour configured anywhere, not one day's: this drives a FILTER
  // that spans dates, and using a single day's list would hide an hour that
  // only exists at weekends.
  const sortedSlots = useMemo(
    () => getAllConfiguredTimeSlots(config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.timeSlots, config?.timeSlotSets],
  );
  const equipmentList = config?.equipment || [];

  // No time slot selected by default — that's the "whole day" view (see
  // equipmentSummary / rentals below). Picking slot(s) narrows both down.
  const toggleTimeSlot = (slot) => {
    setSelectedTimeSlots(prev => (
      prev.includes(slot) ? prev.filter(s => s !== slot) : sortTimeSlots([...prev, slot])
    ));
  };
  const selectAllSlots = () => setSelectedTimeSlots(sortedSlots);
  const clearAllSlots = () => setSelectedTimeSlots([]);

  // Re-subscribe to this date's bookings, equipment holds, and manual-usage
  // whenever the selected date changes — three small, indexed/scoped reads,
  // not a full-table scan. (The time-slot filter below is applied client-
  // side against this same date's data, so it doesn't need its own re-fetch.)
  useEffect(() => {
    if (!db || !selectedDateStr) return undefined;
    setLoading(true);

    const bookingsQuery = query(ref(db, 'bookings'), orderByChild('date'), equalTo(selectedDateStr));
    const unsubBookings = onValue(bookingsQuery, (snapshot) => {
      const list = [];
      if (snapshot.exists()) {
        snapshot.forEach((child) => {
          const data = child.val();
          if (data?.equipmentRental?.items?.length > 0 && ACTIVE_RENTAL_STATUSES.includes(data.status)) {
            list.push({ id: child.key, ...data });
          }
        });
      }
      list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      setBookingsForDate(list);
      setLoading(false);
    });

    const holdsUnsub = onValue(ref(db, `equipmentHolds/${selectedDateStr}`), (snapshot) => {
      setHoldsByEquipment(snapshot.exists() ? snapshot.val() : {});
    });

    const manualUnsub = onValue(ref(db, `equipmentManualUsage/${selectedDateStr}`), (snapshot) => {
      setManualUsage(snapshot.exists() ? snapshot.val() : {});
    });

    return () => {
      unsubBookings();
      holdsUnsub();
      manualUnsub();
    };
  }, [selectedDateStr]);

  // The specific hours the admin has filtered down to. Equipment is
  // returned once its booked slot passes, the same way a court re-opens —
  // so availability only makes sense scoped to these hours, not "today" as
  // a flat total.
  const selectedHours = useMemo(
    () => Array.from(new Set(selectedTimeSlots.map(parseTimeSlotStartHour))),
    [selectedTimeSlots]
  );

  // Last-saved walk-in usage for the current selection: the busiest of the
  // selected hours' saved values (0 if nothing's been selected/saved yet).
  // This is what "Remaining" is computed against — it only moves once Save
  // is clicked, not while the admin is still typing a draft value.
  const walkinBaseline = useMemo(() => {
    const result = {};
    equipmentList.forEach((item) => {
      result[item.id] = selectedHours.length > 0
        ? Math.max(0, ...selectedHours.map(h => Number(manualUsage[item.id]?.[h]) || 0))
        : 0;
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualUsage, selectedHours, config?.equipment]);

  // Per-equipment summary: total owned, and the busiest relevant hour's
  // worth of online holds. With no slot filter, "relevant hour" is the
  // whole day's busiest hour (Total owned / Reserved are the only two
  // fields shown in that state — see render below); once slot(s) are
  // selected it narrows to just those, and Walk-in/Remaining come into play.
  const equipmentSummary = useMemo(() => {
    return equipmentList.map((item) => {
      const total = Number(item.inventoryTotal) || 0;
      const hourly = computeHourlyEquipmentUsage(holdsByEquipment[item.id]);
      const peakReserved = selectedHours.length > 0
        ? Math.max(0, ...selectedHours.map(h => hourly[h] || 0))
        : Math.max(0, ...hourly);
      const usedByWalkins = walkinBaseline[item.id] ?? 0;
      const remaining = Math.max(0, total - peakReserved - usedByWalkins);
      return { ...item, total, peakReserved, usedByWalkins, remaining };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.equipment, holdsByEquipment, selectedHours, walkinBaseline]);

  // Reset the walk-in draft to the saved baseline whenever the slot
  // selection (or date) changes — a fresh filter shouldn't carry over an
  // unsaved edit from a different time window.
  const selectionKey = selectedTimeSlots.join('|');
  useEffect(() => {
    const next = {};
    equipmentList.forEach((item) => { next[item.id] = walkinBaseline[item.id] ?? 0; });
    setWalkinDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey, selectedDateStr]);

  const handleWalkinDraftChange = (item, value) => {
    setWalkinDraft(prev => ({ ...prev, [item.id]: Math.max(0, parseInt(value, 10) || 0) }));
  };

  const isWalkinDirty = (item) => (walkinDraft[item.id] ?? 0) !== (walkinBaseline[item.id] ?? 0);

  // Saves the draft qty to every hour in the current selection at once — a
  // multi-slot selection (e.g. a 2-hour walk-in block) writes the same
  // value across each of those hours.
  const saveWalkin = async (item) => {
    if (selectedHours.length === 0) return;
    const qty = walkinDraft[item.id] ?? 0;
    setSavingId(item.id);
    try {
      const updates = {};
      selectedHours.forEach((h) => {
        updates[`equipmentManualUsage/${selectedDateStr}/${item.id}/${h}`] = qty;
      });
      await update(ref(db), updates);
    } catch (error) {
      console.error('Error saving walk-in usage:', error);
    }
    setSavingId(null);
  };

  const clearWalkinDraft = (item) => {
    setWalkinDraft(prev => ({ ...prev, [item.id]: walkinBaseline[item.id] ?? 0 }));
  };

  // Rentals grouped by time slot — one section per selected slot (in
  // chronological order), each listing the active bookings whose selection
  // included that specific hour. A booking spanning multiple slots
  // naturally appears under each one, the same way its court grid row would.
  // Only used once the admin has actually picked slot(s) to filter down to.
  const rentalsBySlot = useMemo(() => (
    selectedTimeSlots.map(slot => ({
      slot,
      bookings: bookingsForDate.filter(b => bookingHasSlot(b, slot)),
    }))
  ), [selectedTimeSlots, bookingsForDate]);

  // Overall session span across every court/time slot a booking picked —
  // used for the flat "whole day" rentals list when no slot filter is
  // applied, same as the per-slot list but showing each booking once.
  const formatBookingSchedule = (booking) => {
    const slots = sortTimeSlots(Object.keys(booking.timeSlots || {}));
    if (slots.length === 0) return '—';
    const startHour = parseTimeSlotStartHour(slots[0]);
    const endHour = parseTimeSlotStartHour(slots[slots.length - 1]) + 1;
    return `${formatHourLabel(startHour)} - ${formatHourLabel(endHour)}`;
  };

  return (
    <div className="admin-equipment" style={{ color: COLORS.textSecondary }}>
      <h2 className="section-title">🎾 Equipment</h2>
      <p style={{ marginBottom: '1.25rem', opacity: 0.7 }}>
        Equipment gets returned once its booked hour passes, just like a court. Pick a date to see the whole day, or
        narrow down to specific time slot(s) to check availability and rentals for just that window. Prices and
        total units owned are set in Config &rarr; Equipment.
      </p>

      <div className="equipment-filter-bar">
        <div className="form-group" style={{ maxWidth: 220, marginBottom: 0 }}>
          <label>Date</label>
          <input
            type="date"
            value={selectedDateStr}
            onChange={(e) => setSelectedDateStr(e.target.value)}
          />
        </div>

        <div className="equipment-slot-filter">
          <div className="equipment-slot-filter-head">
            <label>Time slot(s)</label>
            <div className="equipment-slot-actions">
              <button type="button" onClick={selectAllSlots} disabled={selectedTimeSlots.length === sortedSlots.length}>
                Select all
              </button>
              <button type="button" onClick={clearAllSlots} disabled={selectedTimeSlots.length === 0}>
                Clear
              </button>
            </div>
          </div>
          <div className="equipment-slot-chips">
            {sortedSlots.map(slot => {
              const active = selectedTimeSlots.includes(slot);
              return (
                <button
                  type="button"
                  key={slot}
                  className={`equipment-slot-chip${active ? ' active' : ''}`}
                  style={active ? { background: COLORS.cyanDeep, borderColor: COLORS.cyanDeep, color: '#fff' } : { borderColor: COLORS.line, color: COLORS.ink }}
                  onClick={() => toggleTimeSlot(slot)}
                >
                  {slot}
                </button>
              );
            })}
            {sortedSlots.length === 0 && <span style={{ opacity: 0.6, fontSize: '0.82rem' }}>No time slots configured yet.</span>}
          </div>
        </div>
      </div>

      {/* Inventory & availability. With no time slot selected, just the
          day's totals (Total owned / Reserved) — Walk-in rentals and
          Available only make sense, and only get edited, once slot(s) are
          picked to scope them to. */}
      <div className="equipment-summary-grid">
        {equipmentSummary.map((item) => {
          const hasSlotFilter = selectedTimeSlots.length > 0;
          const dirty = hasSlotFilter && isWalkinDirty(item);
          return (
            <div className="equipment-card" key={item.id} style={{ borderColor: COLORS.line, background: COLORS.cardSolid }}>
              <h4 style={{ color: COLORS.ink }}>{item.label}</h4>
              <div className="equipment-card-row">
                <span>Total owned</span>
                <strong style={{ color: COLORS.ink }}>{item.total}</strong>
              </div>
              <div className="equipment-card-row">
                <span>Reserved</span>
                <strong style={{ color: COLORS.ink }}>{item.peakReserved}</strong>
              </div>

              {hasSlotFilter && (
                <>
                  <div className="equipment-card-row">
                    <label htmlFor={`walkin-${item.id}`}>Walk-in rentals</label>
                    <input
                      id={`walkin-${item.id}`}
                      type="number"
                      min="0"
                      className="equipment-walkin-input"
                      value={walkinDraft[item.id] ?? 0}
                      onChange={(e) => handleWalkinDraftChange(item, e.target.value)}
                      disabled={savingId === item.id}
                    />
                  </div>
                  {dirty && (
                    <div className="equipment-walkin-actions">
                      <button
                        type="button"
                        className="equipment-walkin-save"
                        style={{ background: COLORS.cyanDeep }}
                        onClick={() => saveWalkin(item)}
                        disabled={savingId === item.id}
                      >
                        {savingId === item.id ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="equipment-walkin-clear"
                        style={{ color: COLORS.inkFaint, borderColor: COLORS.line }}
                        onClick={() => clearWalkinDraft(item)}
                        disabled={savingId === item.id}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="equipment-card-row equipment-card-remaining">
                    <span>Available</span>
                    <strong style={{ color: item.remaining > 0 ? COLORS.cyanDeep : '#c0392b' }}>{item.remaining}</strong>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Rentals — a flat list for the whole day with no filter applied, or
          split into one section per selected time slot once the admin picks
          some. */}
      <h3 style={{ color: COLORS.ink, margin: '1.5rem 0 0.75rem', fontSize: '0.95rem' }}>
        Rentals on {selectedDateStr}
        {selectedTimeSlots.length > 0 && (
          <span style={{ fontWeight: 400, fontSize: '0.78rem', color: COLORS.inkFaint, marginLeft: '0.5rem' }}>
            (filtered to {selectedTimeSlots.length} time slot{selectedTimeSlots.length === 1 ? '' : 's'})
          </span>
        )}
      </h3>

      {loading ? (
        <p style={{ opacity: 0.6 }}>Loading...</p>
      ) : selectedTimeSlots.length === 0 ? (
        bookingsForDate.length === 0 ? (
          <div className="equipment-empty" style={{ borderColor: COLORS.line, background: COLORS.cardSolid }}>
            <p style={{ opacity: 0.6, margin: 0 }}>No equipment rentals for this date.</p>
          </div>
        ) : (
          <div className="equipment-rentals-table-wrapper" style={{ borderColor: COLORS.line, background: COLORS.cardSolid }}>
            <table className="equipment-rentals-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Guest</th>
                  <th>Time</th>
                  <th>Equipment</th>
                  <th style={{ textAlign: 'right' }}>Rental total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {bookingsForDate.map((booking) => (
                  <tr key={booking.id}>
                    <td style={{ fontSize: '0.78rem', color: COLORS.inkFaint }}>{booking.confirmationId}</td>
                    <td style={{ fontWeight: 600, color: COLORS.ink }}>{booking.userName}</td>
                    <td>{formatBookingSchedule(booking)}</td>
                    <td>
                      {booking.equipmentRental.items.map((eq) => (
                        <div key={eq.id} style={{ fontSize: '0.82rem' }}>{eq.label} &times; {eq.qty}</div>
                      ))}
                    </td>
                    <td style={{ textAlign: 'right', color: COLORS.ink, fontWeight: 600 }}>
                      {formatMoney(Number(booking.equipmentRental.total || 0), config)}
                    </td>
                    <td>
                      <span className={`status-badge ${booking.status}`}>{booking.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="equipment-slot-groups">
          {rentalsBySlot.map(({ slot, bookings }) => (
            <div className="equipment-slot-group" key={slot}>
              <div className="equipment-slot-group-header" style={{ color: COLORS.ink, borderColor: COLORS.line }}>
                {formatTimeSlotRange(slot)}
                <span className="equipment-slot-group-count" style={{ color: COLORS.inkFaint }}>
                  {bookings.length} rental{bookings.length === 1 ? '' : 's'}
                </span>
              </div>

              {bookings.length === 0 ? (
                <p className="equipment-slot-empty" style={{ color: COLORS.inkFaint }}>No equipment rentals for this hour.</p>
              ) : (
                <div className="equipment-rentals-table-wrapper" style={{ borderColor: COLORS.line, background: COLORS.cardSolid }}>
                  <table className="equipment-rentals-table">
                    <thead>
                      <tr>
                        <th>Booking ID</th>
                        <th>Guest</th>
                        <th>Time</th>
                        <th>Equipment</th>
                        <th style={{ textAlign: 'right' }}>Rental total</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking.id}>
                          <td style={{ fontSize: '0.78rem', color: COLORS.inkFaint }}>{booking.confirmationId}</td>
                          <td style={{ fontWeight: 600, color: COLORS.ink }}>{booking.userName}</td>
                          <td>{formatTimeSlotRange(slot)}</td>
                          <td>
                            {booking.equipmentRental.items.map((eq) => (
                              <div key={eq.id} style={{ fontSize: '0.82rem' }}>{eq.label} &times; {eq.qty}</div>
                            ))}
                          </td>
                          <td style={{ textAlign: 'right', color: COLORS.ink, fontWeight: 600 }}>
                            {formatMoney(Number(booking.equipmentRental.total || 0), config)}
                          </td>
                          <td>
                            <span className={`status-badge ${booking.status}`}>{booking.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminEquipment;
